import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ONBOARDING_PRODUCTS } from "../../../constants";
import DailyDashboardPreview from "../../dashboard/settings/DailyDashboardPreview";
import LogoOverlayComponent from "../../dashboard/settings/LogoOverlayComponent";
import MobileCalendarPreview from "../../dashboard/settings/MobileCalendarPreview";

// Step 8 — provisioning. Plays a live checklist while the wizard form POSTs to
// /signup-coach (which navigates to /coaching-portal when done).
const BuildingAppStep = ({ customBranding, products = [], onProvision }) => {
  const { t } = useTranslation("auth");

  // Checklist items, conditional on the chosen self-serve products.
  const items = [
    "accountCreated",
    "brandingApplied",
    "libraryLoaded",
    ...(products.includes(ONBOARDING_PRODUCTS.PULL) ? ["pullActivated"] : []),
    ...(products.includes(ONBOARDING_PRODUCTS.PUSH) ? ["pushEnabled"] : []),
    "generatingDemo",
  ];

  const [done, setDone] = useState(0);
  const tick = useRef();
  const submitted = useRef(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: run the checklist animation once on mount.
  useEffect(() => {
    tick.current = setInterval(() => {
      setDone((prev) => {
        if (prev >= items.length) {
          clearInterval(tick.current);
          return prev;
        }
        return prev + 1;
      });
    }, 1400);
    return () => clearInterval(tick.current);
  }, []);

  // Kick off the real provisioning POST once, shortly after mount.
  useEffect(() => {
    if (submitted.current) return;
    submitted.current = true;
    const id = setTimeout(() => onProvision?.(), 600);
    return () => clearTimeout(id);
  }, [onProvision]);

  const {
    splashOverlayColor,
    splashOverlayOpacity,
    withOverlay,
    splashBgSrc,
    splashImgSrc,
    mobileSplashImg,
    tintSplashImg,
    appTheme,
    appMainColor,
    appSecondaryTextColor,
  } = customBranding || {};

  const progress = Math.min(done / items.length, 1);

  return (
    <div className="step-body building-app">
      <div className="build-phones">
        <div className="section phone-template" style={{ left: "7%" }}>
          <div className="background-container">
            <MobileCalendarPreview
              theme={appTheme}
              brandingColor={appMainColor}
              isDefaultColor={false}
              secondaryTextColor={appSecondaryTextColor}
            />
          </div>
          <img alt="phone" src="/img/iphone-template.webp" />
        </div>
        <div className="section phone-template z-10!">
          <div
            className="background-container"
            style={{
              backgroundImage: splashBgSrc ? `url(${splashBgSrc})` : undefined,
              backgroundColor: "var(--background-color)",
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
            }}
          >
            <LogoOverlayComponent
              opacity={splashOverlayOpacity}
              overlayColor={splashOverlayColor}
              withOverlay={withOverlay}
              withLogo
              logo={mobileSplashImg}
              isTinted={tintSplashImg}
              splashLogoSrc={splashImgSrc}
            />
          </div>
          <img alt="phone" src="/img/iphone-template.webp" />
        </div>
        <div className="section phone-template" style={{ right: "7%" }}>
          <div className="background-container">
            <DailyDashboardPreview
              theme={appTheme}
              brandingColor={appMainColor}
              isDefaultColor={false}
              secondaryTextColor={appSecondaryTextColor}
            />
          </div>
          <img alt="phone" src="/img/iphone-template.webp" />
        </div>
      </div>

      <h2>{t("onboardingWizard.buildingApp.title")}</h2>
      <p className="build-subtitle">
        {t("onboardingWizard.buildingApp.subtitle")}
      </p>
      <progress max={1} value={progress} />

      <ul className="build-checklist">
        {items.map((key, idx) => {
          const isDone = idx < done;
          const isCurrent = idx === done;
          return (
            <li
              key={key}
              className={isDone ? "done" : isCurrent ? "current" : "pending"}
            >
              <span className="mark" aria-hidden="true">
                {isDone ? "✓" : "○"}
              </span>
              {t(`onboardingWizard.buildingApp.${key}`)}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default BuildingAppStep;
